import './ProjectDetailsDatasets.scss';
import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { SpaceBetweenLayout } from '../../../../modules/common/layouts/spaceBetweenLayout/SpaceBetweenLayout';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { ProjectDatasetsForm } from '../components/ProjectDatasetsForm';
import { ProjectDetailsDatasetsList } from '../components/ProjectDetailsDatasetsList';
import { IProjectDetailsBl } from '../projectDetails.bl';
import { IProjectAvailableDatasetDto } from '../projectDetails.models';
import { ProjectImageAssignmentPolicyFormContainer } from './ProjectImageAssignmentPolicyForm.container';

export const ProjectDetailsDatasetsContainer = observer((): ReactElement => {
  const projectDetailsBl = useInjection(IProjectDetailsBl.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const { t } = useTranslator();
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | undefined>(undefined);
  const { projectId } = useParams<{ projectId: string }>();

  useEffect(() => {
    const loadProjectDetails = async (): Promise<void> => {
      await withLoaderAsync(async () => projectDetailsBl.getProjectDetailsDatasetsAsync(projectId));
    };
    void loadProjectDetails();
  }, []);

  const refreshState = (): void => {
    setSelectedDatasetId(undefined);
  };

  const nonProjectDatasets = (): IProjectAvailableDatasetDto[] => {
    const projectDatasets = projectDetailsBl.store.datasets;
    return projectDetailsBl.store.availableDatasets
      .filter((ds) => projectDatasets.every((pds) => pds.id !== ds.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleDatasetUnlinkedAsync = async (datasetId: string): Promise<void> => {
    await withLoaderAsync(async () => {
      await projectDetailsBl.unlinkDatasetAsync(datasetId);
      refreshState();
    });
  };

  const handleSelectedDatasetChanged = (selectedDatasetId: string): void => {
    setSelectedDatasetId(selectedDatasetId);
  };

  const onDatasetAddedAsync = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      if (selectedDatasetId === undefined) {
        return;
      }
      await projectDetailsBl.assignDatasetToProjectAsync(selectedDatasetId);
      refreshState();
    }, 'add-dataset-button');
  };

  const handleDatasetOrderChangeAsync = async (from: number, to: number): Promise<void> => {
    await projectDetailsBl.changeDatasetOrderAsync(from, to);
  };

  const projectHasDraftDatasets = projectDetailsBl.store.datasets.some((d) => d.status === 'Draft');

  return (
    <ColumnLayout
      size='md'
      className='project-details-datasets'
    >
      {projectHasDraftDatasets && (
        <S_Alert color='danger'>
          {t('dataset_must_be_published.part_1')}
          <strong>{t('dataset_must_be_published.part_2')}</strong>
          {t('dataset_must_be_published.part_3')}
        </S_Alert>
      )}

      <SpaceBetweenLayout className='controls-container'>
        <ProjectDatasetsForm
          workspaceId={currentWorkspaceStore.currentWorkspace.id}
          projectId={projectDetailsBl.store.id}
          selectedDatasetId={selectedDatasetId}
          datasets={nonProjectDatasets()}
          onDatasetAddedAsync={onDatasetAddedAsync}
          onSelectedDatasetChanged={handleSelectedDatasetChanged}
        />
        <ProjectImageAssignmentPolicyFormContainer />
      </SpaceBetweenLayout>
      <ProjectDetailsDatasetsList
        workspaceId={currentWorkspaceStore.currentWorkspace.id}
        projectDatasets={projectDetailsBl.store.datasets}
        isLoading={isLoading}
        onDatasetUnlinked={handleDatasetUnlinkedAsync}
        onDatasetOrderChange={handleDatasetOrderChangeAsync}
      />
    </ColumnLayout>
  );
});
